import { Paragraph } from '@hungryroot/ramen';
import {
  generateNutritionProductInfoConfig,
  generateRDPModalProductInfoConfig,
  nutritionDisclaimer,
} from '../../configs/RdpConfigs';
import { useRdpIcons } from '../../hooks/useRdpIcons';
import {
  Divider,
  IngredientsList,
  Instructions,
  Nutrition,
  PairingLoader,
  RDPModal,
  XButton,
  nutritionDataToNutritionTable,
} from '../../main';
import { Header } from '../Header/Header';
import styles from '../RdpModalFromPairingId/RdpModalFromPairingId.module.css';

interface UnauthenticatedRDPModalProps {
  id: number;
  onDismiss: () => void;
  overlayClassName?: string;
  onProductClick: (id: number) => void;
}

/** RDP Modal that can be rendered to unauthenticated users. Includes no CTA and no credit Values  */
export const UnauthenticatedRDPModal = (
  props: UnauthenticatedRDPModalProps
) => {
  const { id, onDismiss, overlayClassName, onProductClick } = props;

  const makeRdpIcons = useRdpIcons();

  return (
    <PairingLoader id={id}>
      {({ pairing: pairingV2 }) => {
        const nutrition = pairingV2?.nutrition;
        const name = pairingV2?.name || '';
        const imageUrl = pairingV2?.featured_img_url;
        const recipeScore = pairingV2?.recipe_score;
        const numRatings = pairingV2?.num_ratings;
        const cookingTime = pairingV2?.cooking_time || 0;
        const servings = pairingV2?.servings;
        const badges = pairingV2?.badges;
        const ingredients = pairingV2?.ingredients || [];
        const instructions = pairingV2?.short_instruction_html || '';
        const isLoading = pairingV2 === undefined;
        const slug = pairingV2?.slug || '';

        const { staticShareButton, shareButton } = makeRdpIcons({
          slug,
          name,
          id,
        });

        return (
          <RDPModal
            overlayClassName={overlayClassName}
            isLoading={isLoading}
            ariaLabel={name}
            isOpen={true}
            imageUrl={imageUrl === null ? undefined : imageUrl}
            title={name}
            ingredients={ingredients}
            primaryTopRightIconButton={<XButton onClick={onDismiss} />}
            productInfo={generateRDPModalProductInfoConfig({
              cookingTime,
              numRatings,
              nutrition,
              recipeScore,
              servings,
            })}
            badges={badges}
            onDismiss={onDismiss}
            header={
              <Header
                primaryTopRightIconButton={
                  <XButton
                    onClick={onDismiss}
                    size="medium"
                    prominence="tertiary"
                    style="muted"
                  />
                }
                primaryTopIconButton={staticShareButton}
              />
            }
            primaryTopIconButton={shareButton}
          >
            <Divider />
            <IngredientsList
              onClick={(ingredient) => onProductClick(ingredient.id)}
              ingredients={ingredients?.map((ingredient) => ({
                id: ingredient.id,
                name: ingredient.name,
                imageUrl: ingredient.packaged_image_url,
                subText: ingredient.brand_name,
              }))}
            />
            <Divider />
            <Divider />
            <Instructions
              initialExpansion={true}
              instructions={instructions}
              cookTime={cookingTime}
            />
            <Divider />
            <Nutrition
              servings={
                <>
                  <Paragraph className={styles.secondary}>
                    Servings: {servings}
                  </Paragraph>
                  <Paragraph className={styles['amount-per-serving']}>
                    Amount per serving
                  </Paragraph>
                </>
              }
              productInfo={generateNutritionProductInfoConfig(nutrition)}
              sections={
                nutrition ? nutritionDataToNutritionTable(nutrition) : []
              }
              disclaimer={nutritionDisclaimer}
            />
          </RDPModal>
        );
      }}
    </PairingLoader>
  );
};
